import React, { useEffect, useMemo, useState } from 'react';
import { 
  Box, Card, CardContent, CardHeader, Grid, Typography, Button, Checkbox, 
  Chip, Paper, TextField, MenuItem, Dialog, DialogTitle, DialogContent, 
  DialogActions, FormControl, InputLabel, Select, IconButton, Tooltip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Schedule as ScheduleIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useQuery, useMutation } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const Meetings: React.FC = () => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const formatYmd = (d: Date) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // Local timezone-safe YYYY-MM-DD
  };

  const [selectedDate, setSelectedDate] = useState<string>(() => formatYmd(new Date()));
  const [selectedSupervisorId, setSelectedSupervisorId] = useState<number | ''>(() => (user?.role === 'supervisor' ? user.id : ''));
  const [openGenerateDialog, setOpenGenerateDialog] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    start_time: '09:00',
    end_time: '17:00',
    interval_minutes: 15
  });

  // Supervisor selection
  const { data: supervisors = [] } = useQuery<any[]>(
    'meeting-supervisors',
    async () => {
      const res = await api.get('/users/role/supervisor');
      return res.data.users || [];
    }
  );

  // Auto-select first supervisor for students
  useEffect(() => {
    if (user?.role === 'student' && !selectedSupervisorId && supervisors.length > 0) {
      setSelectedSupervisorId(supervisors[0].id);
    }
  }, [user, selectedSupervisorId, supervisors]);

  // Ensure supervisors default to their own ID once user loads
  useEffect(() => {
    if (user?.role === 'supervisor' && user.id && selectedSupervisorId !== user.id) {
      setSelectedSupervisorId(user.id);
    }
  }, [user, selectedSupervisorId]);

  // For admins, if none selected, choose the first available supervisor when list loads
  useEffect(() => {
    if (user?.role === 'admin' && !selectedSupervisorId && supervisors.length > 0) {
      setSelectedSupervisorId(supervisors[0].id);
    }
  }, [user, selectedSupervisorId, supervisors]);

  // Fetch meeting slots for selected date and supervisor
  const { data: daySlots = [], refetch, isFetching } = useQuery<any[]>(
    ['meeting-slots', selectedDate, selectedSupervisorId, user?.role],
    async () => {
      if (!user || !selectedSupervisorId) return [];
      // Students must use /meetings/available (RBAC allows students here). Supervisors/Admins use /meetings/slots
      const path = user.role === 'student' ? '/meetings/available' : '/meetings/slots';
      const res = await api.get(path, { 
        params: { 
          supervisor_id: selectedSupervisorId, 
          start: selectedDate, 
          end: selectedDate 
        } 
      });
      return res.data.slots || [];
    },
    { enabled: !!user && !!selectedSupervisorId }
  );

  // Fetch current student's bookings to show "Booked" status on their own times
  const { data: myBookings = [], refetch: refetchBookings } = useQuery<any[]>(
    ['my-meeting-bookings'],
    async () => {
      if (!user || user.role !== 'student') return [];
      const res = await api.get('/meetings/my-bookings');
      return res.data.bookings || [];
    },
    { enabled: !!user && user.role === 'student', staleTime: 30_000 }
  );

  // Generate time slots mutation
  const generateSlotsMutation = useMutation(
    async (formData: typeof generateForm) => {
      const supervisorId = user?.role === 'supervisor' ? user.id : selectedSupervisorId;
      if (!supervisorId) throw new Error('No supervisor selected');
      
      await api.post('/meetings/slots/generate', {
        supervisor_id: supervisorId,
        slot_date: selectedDate,
        ...formData
      });
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Time slots generated successfully!', { variant: 'success' });
        setOpenGenerateDialog(false);
        refetch();
      },
      onError: (e: any) => {
        enqueueSnackbar(e.response?.data?.message || 'Failed to generate slots', { variant: 'error' });
      }
    }
  );

  // Toggle slot availability mutation
  const toggleSlot = useMutation(
    async ({ slot_date, slot_time, is_available }: { slot_date: string; slot_time: string; is_available: boolean }) => {
      const body: any = { slot_date, slot_time, is_available };
      if (user?.role === 'admin' && selectedSupervisorId) {
        body.supervisor_id = selectedSupervisorId;
      } else if (user?.role === 'supervisor') {
        body.supervisor_id = user.id;
      }
      await api.post('/meetings/slots/toggle', body);
    },
    {
      onSuccess: () => { 
        enqueueSnackbar('Availability updated', { variant: 'success' }); 
        refetch(); 
      },
      onError: (e: any) => { 
        enqueueSnackbar(e.response?.data?.message || 'Failed to update', { variant: 'error' }); 
      }
    }
  );

  // Book slot (student only)
  const bookSlot = useMutation(
    async (slotId: number) => {
      await api.post('/meetings/book', { slot_id: slotId });
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Meeting booked successfully!', { variant: 'success' });
        refetch();
        // Refresh student's booking cache so status updates to Booked immediately
        refetchBookings && refetchBookings();
      },
      onError: (e: any) => {
        enqueueSnackbar(e.response?.data?.message || 'Failed to book slot', { variant: 'error' });
      }
    }
  );

  // Delete slot mutation
  const deleteSlot = useMutation(
    async (slotId: number) => {
      await api.delete(`/meetings/slots/${slotId}`);
    },
    {
      onSuccess: () => {
        enqueueSnackbar('Slot deleted successfully', { variant: 'success' });
        refetch();
      },
      onError: (e: any) => {
        enqueueSnackbar(e.response?.data?.message || 'Failed to delete slot', { variant: 'error' });
      }
    }
  );

  // Group slots by time for display
  const slotsByTime = useMemo(() => {
    const grouped: { [key: string]: any } = {};
    daySlots.forEach(slot => {
      if (!grouped[slot.slot_time]) {
        grouped[slot.slot_time] = slot;
      }
    });
    return grouped;
  }, [daySlots]);

  // Build a fast lookup of times booked by the current student for the selected date/supervisor
  const myBookedTimes = useMemo(() => {
    if (!user || user.role !== 'student') return new Set<string>();
    const set = new Set<string>();
    myBookings
      .filter((b: any) => b.slot_date === selectedDate && (
        selectedSupervisorId ? b.supervisor_id === selectedSupervisorId : true
      ))
      .forEach((b: any) => set.add(b.slot_time));
    return set;
  }, [user, myBookings, selectedDate, selectedSupervisorId]);

  // Generate time options for the day
  const timeOptions = useMemo(() => {
    const times = [];
    for (let hour = 9; hour <= 17; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        times.push(timeString);
      }
    }
    return times;
  }, []);

  const handleGenerateSlots = () => {
    generateSlotsMutation.mutate(generateForm);
  };

  const handleToggleSlot = (time: string, isAvailable: boolean) => {
    toggleSlot.mutate({
      slot_date: selectedDate,
      slot_time: time,
      is_available: isAvailable
    });
  };

  const handleDeleteSlot = (slotId: number) => {
    if (window.confirm('Are you sure you want to delete this time slot?')) {
      deleteSlot.mutate(slotId);
    }
  };

  const canManageSlots = user?.role === 'admin' || user?.role === 'supervisor';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        <ScheduleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Meeting Scheduler
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {user?.role === 'supervisor' 
          ? 'Manage your availability for student meetings'
          : user?.role === 'admin'
          ? 'Manage meeting schedules for supervisors'
          : 'Book meetings with your supervisor'
        }
      </Typography>

      <Grid container spacing={3}>
        {/* Date and Supervisor Selection */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardHeader title="Schedule Settings" />
            <CardContent>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
              
              {user?.role !== 'supervisor' && (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Supervisor</InputLabel>
                  <Select
                    value={selectedSupervisorId}
                    onChange={(e) => setSelectedSupervisorId(e.target.value as number)}
                    label="Supervisor"
                  >
                    {supervisors.map((supervisor) => (
                      <MenuItem key={supervisor.id} value={supervisor.id}>
                        {supervisor.full_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {canManageSlots && selectedSupervisorId && (
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenGenerateDialog(true)}
                  sx={{ mb: 2 }}
                >
                  Generate Time Slots
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Time Slots Display */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardHeader 
              title={`Available Slots - ${new Date(selectedDate).toLocaleDateString()}`}
              action={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedSupervisorId && (
                    <Typography variant="body2" color="text.secondary">
                      {supervisors.find(s => s.id === selectedSupervisorId)?.full_name}
                    </Typography>
                  )}
                  {user?.role === 'student' && (
                    <Tooltip title={isFetching ? 'Refreshing...' : 'Refresh'}>
                      <span>
                        <IconButton size="small" onClick={() => refetch()} disabled={isFetching}>
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              }
            />
            <CardContent>
              {daySlots.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <ScheduleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No time slots available
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {canManageSlots 
                      ? 'Click "Generate Time Slots" to create availability for this date'
                      : 'No slots have been created for this date yet'
                    }
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={1}>
                  {timeOptions.map((time) => {
                    const slot = slotsByTime[time];
                    const isAvailable = !!slot && (slot.is_available ?? true);
                    const isBookedByMe = user?.role === 'student' && myBookedTimes.has(time);
                    const isBooked = isBookedByMe || (slot?.is_booked ?? false);
                    
                    return (
                      <Grid item xs={6} sm={4} md={3} key={time}>
                        <Paper
                          sx={{
                            p: 2,
                            textAlign: 'center',
                            border: 1,
                            borderColor: isBooked ? 'error.main' : isAvailable ? 'success.main' : 'grey.300',
                            bgcolor: isBooked ? 'error.50' : isAvailable ? 'success.50' : 'grey.50',
                            position: 'relative'
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            {time}
                          </Typography>
                          
                          <Tooltip title={
                            isBooked
                              ? (user?.role !== 'student' && slot?.student_name
                                  ? `Booked by ${slot.student_name}`
                                  : 'Booked')
                              : (isAvailable ? 'Available' : 'Unavailable')
                          }>
                            <Chip
                              label={isBooked ? 'Booked' : isAvailable ? 'Available' : 'Unavailable'}
                              size="small"
                              color={isBooked ? 'error' : isAvailable ? 'success' : 'default'}
                              sx={{ mt: 1 }}
                            />
                          </Tooltip>

                          {user?.role !== 'student' && slot?.student_name && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              {slot.student_name}
                            </Typography>
                          )}

                          {canManageSlots && slot && (
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                              <Checkbox
                                checked={!!isAvailable}
                                onChange={(e) => handleToggleSlot(time, e.target.checked)}
                                size="small"
                              />
                              <Tooltip title="Delete slot">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSlot(slot.id)}
                                  color="error"
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}

                          {!canManageSlots && slot && isAvailable && !isBooked && (
                            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                              <Button 
                                size="small" 
                                variant="contained" 
                                onClick={() => bookSlot.mutate(slot.id)}
                                disabled={bookSlot.isLoading}
                              >
                                {bookSlot.isLoading ? 'Booking...' : 'Book'}
                              </Button>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Generate Slots Dialog */}
      <Dialog open={openGenerateDialog} onClose={() => setOpenGenerateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Time Slots</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={generateForm.start_time}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, start_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={generateForm.end_time}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, end_time: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Interval</InputLabel>
                <Select
                  value={generateForm.interval_minutes}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, interval_minutes: e.target.value as number }))}
                  label="Interval"
                >
                  <MenuItem value={15}>15 minutes</MenuItem>
                  <MenuItem value={30}>30 minutes</MenuItem>
                  <MenuItem value={60}>1 hour</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenGenerateDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleGenerateSlots} 
            variant="contained"
            disabled={generateSlotsMutation.isLoading}
          >
            {generateSlotsMutation.isLoading ? 'Generating...' : 'Generate Slots'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Meetings;